import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faMoneyBillTransfer,
  faPaperPlane,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faHandHoldingDollar,
} from "@fortawesome/free-solid-svg-icons";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  InputGroup,
} from "@themesberg/react-bootstrap";

import { getWithdrawalHistory, instantWithdraw, requestWithdrawal } from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";
  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "approved":
    case "success":
    case "completed":
      return { background: "#10b981", color: "#ffffff" };
    case "pending":
      return { background: "#f59e0b", color: "#ffffff" };
    case "rejected":
    case "failed":
      return { background: "#ef4444", color: "#ffffff" };
    case "processed":
      return { background: "#0ea5e9", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

const mapWithdrawal = (item) => ({
  id: item?.id ?? Date.now(),
  requestId: item?.id ? `WD-${item.id}` : "N/A" || "-",
  date: item?.created_at || "N/A" || "-",
  amount: Number(item?.amount || 0),
  status: String(item?.status || "N/A" || "-"),
  remarks: item?.admin_note || "N/A" || "-",
});

export default function UserWithdrawalHistory() {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [walletBalance, setWalletBalance] = useState(0);
  const [withdrawableAmount, setWithdrawableAmount] = useState(0);
  const [reservedBufferAmount, setReservedBufferAmount] = useState(0);

  // const [formData, setFormData] = useState({
  //   amount: "",
  // });

  // const [errors, setErrors] = useState({
  //   amount: "",
  // });

  const [formData, setFormData] = useState({
    amount: "",
    request_note: "",
  });

  const [errors, setErrors] = useState({
    amount: "",
    request_note: "",
  });

  const [history, setHistory] = useState([]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchWithdrawalHistory = async (
    page = 1,
    perPage = 10,
    searchTerm = ""
  ) => {
    try {
      setLoading(true);

      const response = await getWithdrawalHistory({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const data = response?.data?.data || {};
      const apiWithdrawals = Array.isArray(data?.withdrawals)
        ? data.withdrawals
        : [];
      const apiPagination = data?.pagination || {};

      setWalletBalance(Number(data?.wallet_balance || 0));
      setWithdrawableAmount(Number(data?.withdrawable_amount || 0));
      setReservedBufferAmount(Number(data?.reserved_buffer_amount || 0));

      setHistory(apiWithdrawals.map(mapWithdrawal));
      setPagination({
        current_page: Number(apiPagination.current_page ?? page) || 1,
        last_page: Number(apiPagination.last_page ?? 1) || 1,
        per_page: Number(apiPagination.per_page ?? perPage) || perPage,
        total:
          Number(apiPagination.total ?? apiWithdrawals.length) ||
          apiWithdrawals.length,
      });
    } catch (error) {
      console.error("Failed to fetch withdrawal history:", error);
      toast.error(
        error?.response?.data?.message || "Unable to load withdrawal history."
      );
      setHistory([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
      setWalletBalance(0);
      setWithdrawableAmount(0);
      setReservedBufferAmount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawalHistory(1, pagination.per_page, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchWithdrawalHistory(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // const handleChange = (e) => {
  //   const { name, value } = e.target;

  //   if (name === "amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
  //     return;
  //   }

  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));

  //   setErrors((prev) => ({
  //     ...prev,
  //     [name]: "",
  //   }));
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "amount" && value !== "" && !/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   const amount = Number(formData.amount);
  //   const eligibleAmount = Number(withdrawableAmount) || 0;

  //   const nextErrors = {
  //     amount:
  //       !formData.amount
  //         ? "Withdrawal amount is required."
  //         : amount <= 0
  //           ? "Enter a valid amount."
  //           : amount > eligibleAmount
  //             ? "Amount cannot exceed withdrawable balance."
  //             : "",
  //   };

  //   setErrors(nextErrors);

  //   if (nextErrors.amount) {
  //     toast.error("Please fix the highlighted fields.");
  //     return;
  //   }

  //   try {
  //     setSubmitting(true);

  //     const response = await instantWithdraw({
  //       amount: Number(formData.amount),
  //     });

  //     toast.success(
  //       response?.data?.message || "Withdrawal request submitted successfully."
  //     );

  //     setFormData({
  //       amount: "",
  //     });

  //     await fetchWithdrawalHistory(1, pagination.per_page, search.trim());
  //   } catch (error) {
  //     console.error("Failed to submit withdrawal request:", error);
  //     toast.error(
  //       error?.response?.data?.message || "Unable to submit withdrawal request."
  //     );
  //   } finally {
  //     setSubmitting(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = Number(formData.amount);

    const nextErrors = {
      // amount:
      //   !formData.amount
      //     ? "Withdrawal amount is required."
      //     : amount <= 0
      //       ? "Enter a valid amount."
      //       : "",
      amount:
        !formData.amount
          ? "Withdrawal amount is required."
          : "",
      // request_note: !formData.request_note.trim()
      //   ? "Request note is required."
      //   : "",
    };

    setErrors(nextErrors);

    if (nextErrors.amount || nextErrors.request_note) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const response = await requestWithdrawal({
        amount: Number(formData.amount),
        request_note: formData.request_note.trim(),
      });

      toast.success(
        response?.data?.message || "Withdrawal request submitted successfully."
      );

      setFormData({
        amount: "",
        request_note: "",
      });

      await fetchWithdrawalHistory(1, pagination.per_page, search.trim());
    } catch (error) {
      console.error("Failed to submit withdrawal request:", error);
      toast.error(
        error?.response?.data?.message || "Unable to submit withdrawal request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filteredHistory = useMemo(() => {
    return history;
  }, [history]);

  useEffect(() => {
    const total = filteredHistory.length;
    const last_page = Math.max(1, Math.ceil(total / pagination.per_page));

    setPagination((prev) => ({
      ...prev,
      total,
      last_page,
      current_page: Math.min(prev.current_page, last_page),
    }));
  }, [filteredHistory.length, pagination.per_page]);

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  const pageItems = useMemo(() => {
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;
    const startIndex = (current - 1) * perPage;
    return filteredHistory.slice(startIndex, startIndex + perPage);
  }, [filteredHistory, pagination.current_page, pagination.per_page]);

  return (
    <>
      <div className="withdrawal-page">
        <div className="withdrawal-header py-4">
          <div className="withdrawal-header-top-row">
            {/* <h5 className="mb-0">Withdrawal History</h5> */}
            <h2 className="h4 mb-1">Withdrawal History</h2>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col xs={12} md={4} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Wallet Balance</div>
                  <h4 className="mb-0">{formatCurrency(walletBalance)}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={4} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Withdrawable Amount</div>
                  <h4 className="mb-0">{formatCurrency(withdrawableAmount)}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faMoneyBillTransfer} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={4} xl={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-3 p-md-4 d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Reserved Buffer Amount</div>
                  <h4 className="mb-0">{formatCurrency(reservedBufferAmount)}</h4>
                </div>
                <div className="withdrawal-icon-box">
                  <FontAwesomeIcon icon={faHandHoldingDollar} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} sm={12} xl={12}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    {/* <div className="text-muted small mb-1">Request Payout</div> */}
                    <h5 className="mb-0">Request Payout</h5>
                  </div>
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: 44,
                      height: 44,
                      background: "rgba(44, 96, 186, 0.1)",
                      color: "#005dfb",
                    }}
                  >
                    <FontAwesomeIcon icon={faPaperPlane} />
                  </div>
                </div>

                {/* <div className="mt-4">
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Request Payout Amount <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        name="amount"
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={handleChange}
                        isInvalid={!!errors.amount}
                        style={{ boxShadow: "none" }}
                      />
                      <Button
                        type="submit"
                        className="act-btn"
                        onClick={handleSubmit}
                        disabled={submitting || loading}
                      >
                        {submitting ? "Submitting..." : "Submit Request"}
                      </Button>
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {errors.amount}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div> */}

                <div className="mt-4">
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Request Payout Amount <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        name="amount"
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter amount"
                        value={formData.amount}
                        onChange={handleChange}
                        isInvalid={!!errors.amount}
                        style={{ boxShadow: "none" }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.amount}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">
                        Request Note
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="request_note"
                        placeholder="Enter request note"
                        value={formData.request_note}
                        onChange={handleChange}
                        // isInvalid={!!errors.request_note}
                        style={{ boxShadow: "none" }}
                      />
                      {/* <Form.Control.Feedback type="invalid">
        {errors.request_note}
      </Form.Control.Feedback> */}
                    </Form.Group>

                    <div className="d-flex justify-content-end">
                      <Button
                        type="submit"
                        className="act-btn"
                        disabled={submitting || loading}
                      >
                        {submitting ? "Submitting..." : "Submit Request"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>



        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <div className="withdrawal-section-top withdrawal-history-top">
              <div>
                <h5 className="mb-1">Payout History</h5>
                {/* <div className="text-muted small">
                  Request ID, date, amount, status, and remarks.
                </div> */}
              </div>

              <InputGroup className="withdrawal-search">
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ boxShadow: "none" }}
                />
              </InputGroup>
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    {/* <th>Request ID</th>
                    <th>Date</th> */}
                    <th>Sr No</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-5">
                        Loading payout history...
                      </td>
                    </tr>
                  ) : pageItems.length > 0 ? (
                    pageItems.map((item, index) => (
                      <tr key={item.id}>
                        {/* <td className="fw-semibold">{item.requestId}</td>
                        <td>{formatDateTime(item.date)}</td> */}
                        <td className="fw-semibold">{start + index}</td>
                        <td>{formatCurrency(item.amount)}</td>
                        <td>
                          <Badge
                            className="rounded-pill d-inline-flex align-items-center justify-content-center text-capitalize"
                            style={{
                              ...getStatusBadgeStyle(item.status),
                              minWidth: "5.5rem",
                              height: "2rem",
                              padding: "0 12px",
                            }}
                          >
                            {item.status}
                          </Badge>
                        </td>
                        <td>{item.remarks || "N/A" || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-5">
                        No payout history found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {start} to {end} of {pagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() =>
                    fetchWithdrawalHistory(
                      Math.max(1, pagination.current_page - 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {getPageItems().map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() =>
                        fetchWithdrawalHistory(item, pagination.per_page, search.trim())
                      }
                      className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                        }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page >= pagination.last_page || loading}
                  onClick={() =>
                    fetchWithdrawalHistory(
                      Math.min(pagination.last_page, pagination.current_page + 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <style>{`
        .withdrawal-page {
          width: 100%;
          padding-inline: 0;
        }

        .withdrawal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .withdrawal-header-top-row {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .withdrawal-search {
          width: 100%;
          max-width: 320px;
          flex: 0 1 320px;
          margin-left: auto;
        }

        .withdrawal-icon-box {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(0, 93, 251, 0.1);
          color: #005dfb;
          flex-shrink: 0;
        }

        .withdrawal-section-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .withdrawal-history-top {
          margin-bottom: 1rem;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 991.98px) {
          .withdrawal-search {
            max-width: 280px;
            flex: 0 1 280px;
          }
        }

        @media (max-width: 767.98px) {
          .withdrawal-header {
            flex-wrap: wrap;
            align-items: flex-start;
          }

          .withdrawal-header-top-row {
            flex-direction: column;
            align-items: stretch;
          }

          .withdrawal-search {
            max-width: 100%;
            flex: 1 1 auto;
            margin-left: 0;
          }

          .withdrawal-section-top {
            flex-direction: column;
            align-items: stretch;
          }

          .withdrawal-history-top {
            flex-direction: row;
            align-items: center;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }
        }

        @media (max-width: 575.98px) {
          .withdrawal-history-top {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </>
  );
}